/* eslint-disable jsx-a11y/no-access-key */
import { useCallback, useState } from 'react'

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Input,
  Typography,
} from '@mui/material'
import { Button, useNotify, useTranslate } from 'react-admin'

import { ArtworkApiDTO } from '../../codegen/generated/Api.schemas'
import { usePostApiCompilations } from '../../codegen/generated/compilations'
import { CompilationItemsList } from './CompilationItemsList'
import { CompilationMainArtwork } from './CompilationMainArtwork'
import styles from './Compilations.module.scss'

type CreateCompilationFormData = {
  name: string
  mainArtwork: ArtworkApiDTO | undefined
  linkedArtworksIds: ArtworkApiDTO['id'][]
}

export type CreateCompilationModalProps = {
  isModalOpen: boolean
  setIsModalOpen: (b: boolean) => void
  onSuccess: () => void
}

export const CreateCompilationModal = ({
  isModalOpen,
  setIsModalOpen,
  onSuccess,
}: CreateCompilationModalProps): JSX.Element => {
  const notify = useNotify()
  const translate = useTranslate()

  const [formData, setFormData] = useState<CreateCompilationFormData>({
    name: '',
    mainArtwork: undefined,
    linkedArtworksIds: [],
  })

  const compilationMutation = usePostApiCompilations()

  const onCreateCompilation = useCallback(async () => {
    try {
      const { name, mainArtwork, linkedArtworksIds } = formData
      if (mainArtwork) {
        await compilationMutation.mutateAsync({
          data: {
            name,
            mainArtworkId: mainArtwork.id,
            artworks: linkedArtworksIds.map(artworkId => ({
              artworkId,
              compilationShare: (1 / linkedArtworksIds.length).toString(),
            })),
          },
        })

        onSuccess()
        setIsModalOpen(false)
        notify(translate('pages.compilations.createCompilation.toast_createCompilationSuccess'), {
          type: 'success',
        })
      }
    } catch (e: any) {
      console.error(`Error - create compilation - ${e}`)
      notify(
        `${translate('pages.compilations.createCompilation.toast_createCompilationError')} - ${
          e.response?.data?.message ? e.response.data.message : e
        }`,
        { type: 'error' },
      )
    }
  }, [compilationMutation.mutateAsync, formData, notify, translate, onSuccess, setIsModalOpen])

  const onChange = (name: keyof typeof formData, value: any) => {
    setFormData({ ...formData, [name]: value })
  }

  return (
    <Dialog
      aria-labelledby="create-compilation-modal"
      open={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle id="create-compilation-modal">
        {translate('pages.compilations.createCompilation.title')}
      </DialogTitle>
      <DialogContent dividers>
        <div className={styles.message}>
          <Grid container direction="row" spacing={5} marginBottom={5}>
            <Grid container item xs={3}>
              <Typography fontWeight="bold">{translate('_generics.name')}</Typography>
              <FormControl
                fullWidth
                sx={{
                  alignSelf: 'flex-end',
                }}
              >
                <Input
                  id="name"
                  name="name"
                  aria-describedby="name"
                  value={formData.name}
                  onChange={e => onChange('name', e.currentTarget.value)}
                />
              </FormControl>
            </Grid>
            <Grid container item xs={9} spacing={0}>
              <Typography fontWeight="bold">
                {translate('pages.compilations.mainArtwork')}
              </Typography>
              <CompilationMainArtwork
                onUpdate={mainArtwork => {
                  onChange('mainArtwork', mainArtwork)
                }}
              />
            </Grid>
          </Grid>
          <CompilationItemsList onUpdate={ids => onChange('linkedArtworksIds', ids)} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setIsModalOpen(false)
          }}
          label={translate('_generics.cancel')}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={onCreateCompilation}
          label={translate('_generics.create')}
          disabled={
            compilationMutation.isLoading ||
            !formData.name ||
            !formData.mainArtwork ||
            formData.linkedArtworksIds.length === 0
          }
        />
      </DialogActions>
    </Dialog>
  )
}
